import React, { useMemo, useEffect, useRef } from 'react'
import { Global, css } from '@emotion/react'
import clsx from 'clsx'
import qs from 'qs'
import { upload } from '@vt/apis/lib-ex/v2/givrali'
import { useTbViewStore, useProjectStore, useAppEx } from '@vt/hooks'
import { pick, get, findIndex } from 'lodash-es'
import { Toast } from '@ow/comps'
import { Table } from '@ow/ditto'
import { getQuery, getAppIdFromURL } from '@vt/utils'
import OperateComponent from '../../widgets/operate'
import FixedOperateComponent from '../../widgets/FixedOperate'
import { useDataOperateStore } from '../../data-operate/data-operate-store-provider'

// 暂时写死吧
const xcAppId = process.env.REACT_APP_DEP === 'dev' ? 975 : 980

const TableContainer = function ({
  sorts,
  beforeTransform,
  afterTransform,
  showTbDetail,
  editTbData,
  onShowStatistics,
  CustomOperateComponent,
}) {
  const { oid, sharedId } = getQuery()
  const dataRef = useRef([])
  const { appId } = useAppEx()
  const {
    fields,
    tbData,
    listConfig,
    ditto,
    selectedIds,
    columnShowType,
    editorConfig,
    destroyTbData,
    recoverTbData,
    setSelectedIds,
    setSorts,
    setOrder,
    loading,
    operatorConfig,
    setColumnShowType,
    showOperation,
    showRecycle,
    tbSlug,
    updateNodeOrder,
    updateTbData,
    dataCheckDisabled,
    rowHeight,
  } = useTbViewStore((state) =>
    pick(state, [
      'fields',
      'tbData',
      'listConfig',
      'selectedIds',
      'columnShowType',
      'editorConfig',
      'destroyTbData',
      'updateTbData',
      'recoverTbData',
      'setSelectedIds',
      'loading',
      'ditto',
      'operatorConfig',
      'showRecycle',
      'setSorts',
      'setOrder',
      'setColumnShowType',
      'showOperation',
      'tbSlug',
      'updateNodeOrder',
      'dataCheckDisabled',
      'rowHeight',
    ])
  )

  const { inlineEdit } = editorConfig || {}

  const customCompsMap = useProjectStore((state) => state.customCompsMap)

  const {
    OperateComponent: CommonOperateComponent,
    customOperateSlugs,
    // showFixedOperation,
    fixedOperationWidths,
  } = customCompsMap || {}

  const { operators, refreshTree, isDraggable } = useDataOperateStore((state) =>
    pick(state, ['operators', 'refreshTree', 'isDraggable'])
  )
  const { showTbInlineEdit } = operators || {}
  const { stylesConfig = {} } = listConfig || {}

  const config = useMemo(() => {
    return {
      ...listConfig,
      fields: columnShowType,
    }
  }, [listConfig, columnShowType])

  // 分享模式下只展示查看详情按钮，考虑对recycle view扩展，增加share类型
  const operatorList = useMemo(() => {
    if (!sharedId) return operatorConfig
    return {
      operatorConfig: {
        ...operatorConfig?.operatorConfig,
        elements: operatorConfig?.operatorConfig?.elements.filter(
          (c) => c.actionType === 'detailConfig'
        ),
      },
    }
  }, [sharedId, operatorConfig])

  // 是否展示操作列
  const operatorAllowed = useMemo(() => {
    const currentOperatorConfig = showRecycle
      ? operatorConfig.recycle
      : operatorConfig.operatorConfig
    return (
      (CommonOperateComponent && (customOperateSlugs || []).includes(tbSlug)) ||
      currentOperatorConfig
    )
  }, [showRecycle])

  const operatorNum = useMemo(() => {
    const currentOperatorConfig = showRecycle
      ? operatorConfig.recycle
      : operatorConfig.operatorConfig
    return get(currentOperatorConfig, 'elements', []).length
  }, [showRecycle])

  const onChangeOrder = (order) => {
    setOrder(order)
    updateNodeOrder()
      .then(() => {
        if (refreshTree) {
          refreshTree()
        }
      })
      .catch((err) => {
        Toast.error({
          title: '操作失败',
          description: err.error,
        })
      })
  }

  const onClickNextOrPrev = (rowData, tbDataIndex, setTbDataIndex) => {
    const arr = dataRef.current || []
    if (!tbDataIndex && tbDataIndex !== 0) {
      const nowIndex = findIndex(arr, (v) => v.objectId === rowData.objectId)
      setTbDataIndex(nowIndex + 1)

      if (nowIndex + 1 < arr.length) {
        return arr[nowIndex + 1]
      }
      return null
    }
    if (tbDataIndex + 1 < arr.length) {
      setTbDataIndex(tbDataIndex + 1)
      return arr[tbDataIndex + 1]
    }
    return null
  }

  const updateMarkImg = async (files, _currentFile, onCancle) => {
    const formData = new FormData()
    formData.append('files', files)
    formData.append('appId', getAppIdFromURL())

    const fileData = await upload(formData)
    if (fileData.data) {
      const newImgSrc = []
      fileData.data.forEach((obj) => {
        if (obj) {
          const [llink, queries] = obj.llink.split('?')
          const currentFile = {
            filename:
              obj.extendedData && obj.extendedData.originalName
                ? obj.extendedData.originalName
                : '',
            name: obj.name,
            src: llink,
            urlSuffixMap: queries && qs.parse(queries),
            fileType: obj.extendedData.exif.ExpectedFileNameExtension,
            time: Date.now(),
          }
          newImgSrc.push(currentFile)
        }
      })

      if (_currentFile.data?.objectId) {
        return updateTbData({
          objectId: _currentFile.data.objectId,
          [_currentFile.slug]: newImgSrc,
        }).then((nowData) => {
          Toast.success({
            title: '标注成功',
          })
          onCancle()
          return nowData
        })
      }
    }
    return null
  }

  useEffect(() => {
    if (oid) {
      showTbDetail({ objectId: oid })
    }
    dataRef.current = tbData
  }, [tbData])

  return (
    <>
      {/* 不能用global */}
      <Global
        styles={css`
          .hidden-operate-table {
            .BaseTable__operate-table-frozen-right {
              display: none !important;
            }
          }
          .none-data {
            .BaseTable__table-main {
              overflow: auto !important;
              .BaseTable__body {
                overflow: unset !important;
              }
              .BaseTable__header {
                overflow: unset !important;
              }
            }
          }
        `}
      />
      <div className="w-full h-full relative hidden md:block">
        {/* 参数怎么越来越多 */}
        <Table
          fields={fields}
          tbData={tbData}
          loading={loading}
          isShowSelections={listConfig?.checkable !== false}
          selectedIds={selectedIds}
          mergeCellInfo={ditto.mergeCellInfo}
          defaultSorts={sorts}
          inlineEdit={inlineEdit}
          beforeTransform={beforeTransform}
          afterTransform={afterTransform}
          onChangeSelected={setSelectedIds}
          onChangeSorts={setSorts}
          onChangeOrder={onChangeOrder}
          config={config}
          showTbDetail={showTbDetail}
          createTbData={editTbData}
          showTbInlineEdit={showTbInlineEdit}
          destroyTbData={destroyTbData}
          recoverTbData={recoverTbData}
          switchDataNextOrPrev={appId === xcAppId ? onClickNextOrPrev : null}
          updateMarkImg={updateMarkImg}
          operatorConfig={operatorList}
          operatorNum={operatorNum}
          OperateComponent={CustomOperateComponent || OperateComponent}
          FixedOperateComponent={FixedOperateComponent} // 固定列操作
          columnShowType={columnShowType}
          onChangeColumnShowType={setColumnShowType}
          onShowStatistics={onShowStatistics}
          showOperation={showOperation}
          showFixedOperation
          isRenderOperateTable={false}
          dataCheckDisabled={dataCheckDisabled}
          // isRenderOperateTable={!operatorConfig?.hidden && !showFixedOperation}
          fixedOperationWidth={
            fixedOperationWidths && fixedOperationWidths[tbSlug]
          }
          isDraggable={isDraggable}
          iSestimatedRowHeight={stylesConfig.selfAdaption}
          rowHeight={rowHeight}
          className={clsx([
            !operatorAllowed ? 'hidden-operate-table' : '',
            tbData.length ? '' : 'none-data',
            stylesConfig && stylesConfig.selfAdaption
              ? 'table-self-adaption'
              : '',
          ])}
        />
      </div>
    </>
  )
}

export default TableContainer
